import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import '../styles/caraousel.css';

// Correct image imports
import car1 from '../images/car1.png';
import car2 from '../images/car2.png';
import car3 from '../images/car3.png';
import car4 from '../images/car4.png';

const AnimatedCharacter = ({ char, index, baseDelay, isOrange = false, customStyles = {} }) => {
  return (
    <motion.span
      key={index}
      initial={{ 
        opacity: 0, 
        x: -50,
        rotate: -10
      }}
      whileInView={{ 
        opacity: 1, 
        x: 0,
        rotate: 0
      }}
      transition={{
        delay: baseDelay + index * 0.05,
        type: "spring",
        stiffness: 100,
        damping: 10
      }}
      viewport={{ once: false }}
      style={{
        display: 'inline-block',
        willChange: 'transform, opacity',
        margin: char === ' ' ? '0 0.25em' : '0', 
        color: isOrange ? '#FF6B35' : 'inherit',
        ...customStyles
      }}
      className={`char-${index + 1}`}
    >
      {char}
    </motion.span>
  );
};

const WhyChooseUsHeading = () => {
  const whyText = "WHY";
  const chooseText = "CHOOSE US?";

  return (
    <motion.div
      className="about-header"
      initial={{ opacity: 0, y: -50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, type: "spring" }}
      viewport={{ once: false }}
    >
      <div className="about-heading">
        <div className="heading-wrapper">
          {whyText.split("").map((char, index) => (
            <AnimatedCharacter
              key={`why-${index}`}
              char={char}
              index={index}
              baseDelay={0.2}
              customStyles={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(2.5rem, 5vw, 5rem)',
                color: '#ffffff'
              }}
            />
          ))}
        </div>
        <div className="heading-wrapper">
          {chooseText.split("").map((char, index) => (
            <AnimatedCharacter
              key={`choose-${index}`}
              char={char}
              index={index}
              baseDelay={0.4}
              isOrange={char !== " "}
              customStyles={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(2.5rem, 5vw, 5rem)'
              }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const WhyChooseUsCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: 'Cutting-Edge Technology Solutions',
      description: 'Our innovative approach combines state-of-the-art technologies with strategic insights, delivering transformative solutions that propel your business into the future of digital excellence.',
      image: car1,
      gradient: 'from-blue-900 to-blue-600'
    },
    {
      title: 'Personalized IT Strategies',
      description: 'We craft bespoke technological roadmaps that align perfectly with your unique business objectives, ensuring maximum efficiency, scalability, and competitive advantage.',
      image: car2,
      gradient: 'from-purple-900 to-purple-600'
    },
    {
      title: 'Comprehensive Support Ecosystem',
      description: 'Beyond traditional IT services, we offer an end-to-end support ecosystem that provides continuous innovation, proactive maintenance, and strategic technological guidance.',
      image: car3,
      gradient: 'from-teal-900 to-teal-600'
    },
    {
      title: 'Future-Proof Innovation',
      description: 'We don\'t just solve today\'s challenges; we anticipate tomorrow\'s opportunities. Our forward-thinking approach ensures your technological infrastructure remains adaptive and resilient.',
      image: car4,
      gradient: 'from-red-900 to-red-600'
    }
  ];

  // Auto-advance slides
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);  // Increased slide time for better readability

    return () => clearInterval(slideInterval);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="why-choose-us-section">
      <WhyChooseUsHeading />

      <div className="carousel-container">
        <AnimatePresence mode="wait">
          <motion.div 
            key={currentSlide}
            className="carousel-slide"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 30,
              duration: 0.6
            }}
          >
            <div 
              className={`slide-content ${slides[currentSlide].gradient}`}
            >
              <div className="slide-text-container">
                <motion.div 
                  className="slide-text"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                >
                  <h2 className="slide-title">{slides[currentSlide].title}</h2>
                  <p className="slide-description">{slides[currentSlide].description}</p>
                </motion.div>
              </div>
              
              <div className="slide-image-container">
                <motion.div 
                  className="slide-image-wrapper"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                >
                  <img 
                    src={slides[currentSlide].image} 
                    alt={`Slide ${currentSlide + 1}`}
                    className="carousel-image"
                  />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="carousel-navigation">
          <motion.button 
            onClick={prevSlide}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="nav-button left-nav"
          >
            <FaChevronLeft />
          </motion.button>
          
          <motion.button 
            onClick={nextSlide}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="nav-button right-nav"
          >
            <FaChevronRight />
          </motion.button>
        </div>

        <div className="slide-indicators">
          {slides.map((_, index) => (
            <motion.div
              key={index}
              className={`indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WhyChooseUsCarousel;